// Sunday-aligned week axis for weekly models.
//
// Weeks start on Monday and are labelled by the date of their last day
// (Sunday), which is what the Week Definition dialog in CreateSimulation.vue
// states. The axis therefore runs over the Sundays from dateFrom up to the
// Sunday that closes the week containing dateTo.
//
// The upper bound is the week that CONTAINS the model's last available date
// (config dates.end), not that date itself. When dates.end falls on any day
// other than a Sunday, the Sunday closing its week comes after it: that week is
// partly inside and partly outside the model range, and it still has to be a
// column, because the simulator derives the same axis from the plan's
// date_from / date_to and expects a value for it. Bounding on dates.end itself
// dropped that week and left the media plan one column short, which is what
// made the simulator fail on models whose data does not end on a Sunday.
//
// A dateTo beyond dates.end is still bounded, which is what that stricter
// bound was originally added for: base.date_to is not re-validated against the
// model's current range when an older plan is copied or edited.

// Parses YYYY-MM-DD into a local-zone Date at noon (january == 0). Noon keeps a
// DST shift from moving a date across midnight while the axis is walked day by
// day.
function localNoonDate (date) {
  const parts = date.slice(0, 10).split('-')
  return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]), 12, 0, 0)
}

// Advances a Date in place to the first sunday on or after it (sunday == 0).
function advanceToSunday (date) {
  while (date.getDay() !== 0) {
    date.setDate(date.getDate() + 1)
    // Reset time to 12:00:00 so DST doesn't mess things up
    date.setHours(12, 0, 0)
  }
  return date
}

// Sundays covering [dateFrom, dateTo], bounded by the week containing modelEnd
// (optional). Returns an array of YYYY-MM-DD strings, empty when either end of
// the range is missing or when the range contains no week end.
export function weeklyPeriodDates (dateFrom, dateTo, modelEnd) {
  if (!dateFrom || !dateTo) return []
  const currentDate = localNoonDate(dateFrom)
  let stopDate = advanceToSunday(localNoonDate(dateTo))
  if (modelEnd) {
    const lastDate = advanceToSunday(localNoonDate(modelEnd))
    if (lastDate.getTime() < stopDate.getTime()) {
      stopDate = lastDate
    }
  }
  const dates = []
  while (currentDate.getTime() <= stopDate.getTime()) {
    // currentDate must always be a sunday (sunday == 0)
    if (currentDate.getDay() === 0) {
      // sv-SE date format is YYYY-MM-DD
      dates.push(Intl.DateTimeFormat('sv-SE').format(currentDate))
    }
    // Add a day
    currentDate.setDate(currentDate.getDate() + 1)
    // Reset time to 12:00:00 so DST doesn't mess things up
    currentDate.setHours(12, 0, 0)
  }
  return dates
}
